import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { Product, Category } from '../types';
import { api } from '../services/api';
import buildData from '../data/build-data.json';
import { BuildData } from '../data/build-data-types';

interface DataContextType {
  products: Product[];
  featuredProducts: Product[];
  updateProduct: (product: Product) => void;
  addProduct: (product: Product) => void;
  deleteProduct: (id: string) => void;
  categories: Category[];
  updateCategory: (category: Category) => void;
  addCategory: (category: Category) => void;
  deleteCategory: (id: string) => void;
  loading: boolean;
  error: string | null;
  loadAllProducts: () => Promise<void>;
}

const DataContext = createContext<DataContextType | undefined>(undefined);

export const useData = () => {
  const context = useContext(DataContext);
  if (!context) {
    throw new Error('useData must be used within a DataProvider');
  }
  return context;
};

interface DataProviderProps {
  children: ReactNode;
}

const migrateProducts = (products: any[]): Product[] => {
  return products.map((product, index) => {
    // If product has old priceMultiplier structure, migrate it
    if (product.materials && product.materials.length > 0 && product.materials[0].priceMultiplier !== undefined && product.materials[0].pricingType === undefined) {
      product.materials = product.materials.map((material: any) => ({
        ...material,
        pricingType: 'multiplier',
        priceMultiplier: material.priceMultiplier,
        fixedCost: undefined
      }));
    }
    
    // Ensure materials, finishingOptions, and colorSpecs arrays exist
    if (!product.materials) {
      product.materials = [];
    }
    if (!product.finishingOptions) {
      product.finishingOptions = [];
    }
    if (!product.colorSpecs) {
      product.colorSpecs = [];
    }
    
    // Add displayOrder if it doesn't exist
    if (product.displayOrder === undefined) {
      product.displayOrder = index;
    }
    
    // Ensure fixedSizes array exists for non-customizable products
    if (!product.fixedSizes) {
      product.fixedSizes = [];
    }
    
    // Ensure customOptions array exists
    if (!product.customOptions) {
      product.customOptions = [];
    }
    
    // Add active field if it doesn't exist (default to true)
    if (product.active === undefined) {
      product.active = true;
    }
    
    return product;
  });
};

export const DataProvider: React.FC<DataProviderProps> = ({ children }) => {
  const [products, setProducts] = useState<Product[]>([]);
  const [featuredProducts, setFeaturedProducts] = useState<Product[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [allProductsLoaded, setAllProductsLoaded] = useState(false);

  // Initialize with build data immediately
  useEffect(() => {
    const typedBuildData = buildData as BuildData;
    
    // Set categories from build data
    if (typedBuildData.categories && typedBuildData.categories.length > 0) {
      setCategories(typedBuildData.categories as Category[]);
    }
    
    // Set featured products from build data
    if (typedBuildData.featuredProducts && typedBuildData.featuredProducts.length > 0) {
      const migratedFeatured = migrateProducts(typedBuildData.featuredProducts as any[]);
      setFeaturedProducts(migratedFeatured);
      // Initialize products with featured products for now
      setProducts(migratedFeatured);
    }
    
    // Log build data info
    console.log(`Using build data from ${typedBuildData.buildTime} (${typedBuildData.environment})`);
    
    // Check if build data is stale (older than 24 hours)
    const buildTime = new Date(typedBuildData.buildTime);
    const now = new Date();
    const hoursSinceBuild = (now.getTime() - buildTime.getTime()) / (1000 * 60 * 60);
    
    if (hoursSinceBuild > 24 || typedBuildData.error) {
      console.warn('Build data is stale or has errors, consider refreshing from API');
    }
  }, []);

  // Function to load all products (for admin pages)
  const loadAllProducts = async () => {
    if (allProductsLoaded) return; // Don't reload if already loaded
    
    try {
      setLoading(true);
      setError(null);
      
      const apiProducts = await api.products.getAll();
      setProducts(migrateProducts(apiProducts));
      setAllProductsLoaded(true);
      
      // Update categories from API too if needed
      try {
        const apiCategories = await api.categories.getAll();
        setCategories(apiCategories);
      } catch (err) {
        console.log('Failed to refresh categories from API');
      }
    } catch (err) {
      console.error('Failed to load all products:', err);
      setError('Failed to load products');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const updateProduct = async (updatedProduct: Product) => {
    try {
      setError(null);
      const updated = await api.products.update(updatedProduct.id, updatedProduct);
      setProducts(prev => prev.map(p => p.id === updatedProduct.id ? updated : p));
    } catch (err) {
      console.error('Failed to update product:', err);
      setError('Failed to update product');
    }
  };

  const addProduct = async (product: Product) => {
    try {
      setError(null);
      const newProduct = await api.products.create(product);
      setProducts(prev => [...prev, newProduct]);
    } catch (err) {
      console.error('Failed to add product:', err);
      setError('Failed to add product');
    }
  };

  const deleteProduct = async (id: string) => {
    try {
      setError(null);
      await api.products.delete(id);
      setProducts(prev => prev.filter(p => p.id !== id));
    } catch (err) {
      console.error('Failed to delete product:', err);
      setError('Failed to delete product');
    }
  };

  const updateCategory = async (updatedCategory: Category) => {
    try {
      setError(null);
      const updated = await api.categories.update(updatedCategory.id, updatedCategory);
      setCategories(prev => prev.map(c => c.id === updatedCategory.id ? updated : c));
    } catch (err) {
      console.error('Failed to update category:', err);
      setError('Failed to update category');
    }
  };

  const addCategory = async (category: Category) => {
    try {
      setError(null);
      const newCategory = await api.categories.create(category);
      setCategories(prev => [...prev, newCategory]);
    } catch (err) {
      console.error('Failed to add category:', err);
      setError('Failed to add category');
    }
  };

  const deleteCategory = async (id: string) => {
    try {
      setError(null);
      await api.categories.delete(id);
      setCategories(prev => prev.filter(c => c.id !== id));
    } catch (err) {
      console.error('Failed to delete category:', err);
      setError('Failed to delete category');
    }
  };

  const value = {
    products,
    featuredProducts,
    updateProduct,
    addProduct,
    deleteProduct,
    categories,
    updateCategory,
    addCategory,
    deleteCategory,
    loading,
    error,
    loadAllProducts,
  };

  return <DataContext.Provider value={value}>{children}</DataContext.Provider>;
};